/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
// import { Link } from 'gatsby';
import parse, { DOMNode, domToReact, Element } from 'html-react-parser';
import ReactDOMServer from 'react-dom/server';
import React from 'react';
import { scrollTo } from '@ecs/tng-common/scripts/helpers';
import { IContentGroup /* , PostContentParams */ } from './interfaces';
import { isInternalLink } from './functions';
import { addLinkTrackingAttributes } from './analytics';

export const ficoTransformed = 'FICO<sup>®</sup>';
export const vantageTransformed = 'VantageScore<sup>®</sup>';
export const boostTransformed = 'Experian Boost<sup>®</sup>';

export const addDisclosureAnchorIcon = () => {
  return (
    <button
      type="button"
      onClick={() => {
        scrollTo('footer-disclosures');
      }}
      className="ecs-font-icon-act-info text-secondary btn btn-link body-text"
      aria-label="See disclosures."
    />
  );
};
export const checkIfToIncludeBoostDisclosure = (content: string) => {
  return /Experian Boost/.test(content);
};
export const checkIfToIncludeVantageDisclosure = (content: string) => {
  return /VantageScore/.test(content);
};
export function sanitizeAndCombineText(text: string) {
  // Remove HTML tags and special symbols
  const sanitizedText = text.replace(/<\/?[^>]+(>|$)/g, '').replace(/[^\w\s]/gi, '');
  // Combine words using hyphen
  return sanitizedText.split(/\s+/).join('-');
}

function nodeToHTML(node: Element): string {
  if (!node || !node.children) return '';

  return node.children
    .map((child) => {
      switch (child.type) {
        case 'text': // Text nodes
          return child.data || '';

        case 'tag': {
          // Element nodes
          const attributes = Object.entries(child.attribs || {})
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');

          const childrenHTML = nodeToHTML(child); // Recursive call for children

          return `<${child.name}${attributes ? ' ' + attributes : ''}>${childrenHTML}</${child.name}>`;
        }

        default:
          return ''; // Ignore unsupported nodes
      }
    })
    .join(''); // Combine all children
}
export const processCommonNodes = (node: Element, params?: any) => {
  // Handle internal links if href attribute exists,
  // add  data-link-cs={`ae-link-c${params.categoryId}-${params.postId}-${combinedText}`} //if requested
  if (node?.name === 'a' && node?.attribs?.href && isInternalLink(node?.attribs?.href)) {
    /*  const combinedText = sanitizeAndCombineText(
      node.children.map((ch: any) => ch.data || '').join('')
    );
    // REPLACE WITH GATSBY LINK
    return (
      <Link
        to={node.attribs.href}
        {...node.attribs}>
        {domToReact(node.children as DOMNode[])}
      </Link>
    ); */
    // Add product link tracking
    addLinkTrackingAttributes(node, params?.categoryId, params?.postId);
  }

  // Find span.disc-info-icon and replace with disclosure icon
  if (
    node?.name === 'span' &&
    node?.attribs?.class &&
    node?.attribs?.class?.includes('disc-info-icon')
  ) {
    return addDisclosureAnchorIcon();
  }

  return node;
};
export function generateAccordion(
  node: Element /* , params: PostContentParams */
): IContentGroup[] {
  const processAccordion = (
    itemSelector: string,
    titleSelector: string,
    contentSelector: string
  ): IContentGroup[] => {
    const accordions: IContentGroup[] = [];

    const accordionItems = node.children.filter(
      (child: any) => child.type === 'tag' && child.attribs?.class === itemSelector
    );

    accordionItems.forEach((accordionItem: any, index: number) => {
      const titleNode = accordionItem?.children?.find(
        (child: any) => child.type === 'tag' && child.name === titleSelector
      );

      const contentNode = accordionItem?.children?.find(
        (child: any) => child.type === 'tag' && child.attribs?.class === contentSelector
      );
      const content = parse(nodeToHTML(contentNode), {
        replace: (nodeChild: DOMNode) => processCommonNodes(nodeChild as Element /* , params */)
      });
      accordions.push({
        title: nodeToHTML(titleNode),
        content: content,
        id: `${index}-acc-item`
      });
    });

    return accordions;
  };

  const newAccordions = processAccordion('blog-accordion-element', 'h3', 'blog-accordion-panel');

  return newAccordions;
}
export function generateTabContent(node: Element): IContentGroup[] {
  const tabs: any = node.children.find(
    (child: any) => child.name === 'div' && child.attribs?.class === 'table-nav'
  );
  const tabTitles: Element = tabs.children.find(
    (child: any) => child.name === 'ul' && child.attribs?.class === 'tabs-nav-tabs'
  );
  const tabContents = tabs.children.filter(
    (child: any) => child.name === 'div' && child.attribs?.class === 'tabs-nav-content'
  );
  const tabContentData: IContentGroup[] = tabTitles.children
    .filter((child) => child.type === 'tag' && child.name === 'li')
    .map((li: any, index: number) => {
      const tabContent = tabContents[index];
      const tabTitle = li?.children?.[0]?.data || '';
      const cont = parse(ReactDOMServer.renderToStaticMarkup(domToReact(tabContent.children)), {
        replace: (nodeChild: DOMNode) => processCommonNodes(nodeChild as Element /* , params */)
      });
      return {
        id: `item-${index}`,
        title: tabTitle,
        content: cont
      };
    });
  return tabContentData;
}
export function generateQuidgetContent(node: Element) {
  const getContent = (node: Element) => {
    return parse(ReactDOMServer.renderToStaticMarkup(domToReact(node.children as DOMNode[])), {
      replace: (nodeChild: DOMNode) => processCommonNodes(nodeChild as Element /* , params */)
    });
  };
  // Get all quidget card elements
  const quidgetCards =
    node.children.filter(
      (child: any) =>
        child.type === 'tag' && child.name === 'div' && child.attribs?.class === 'quidget-card'
    ) || [];
  const quidgetContent: any[] = quidgetCards.map((card: any, index: number) => {
    // Extract card head structure
    const quidgetHead = card.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'quidget-head'
    );
    // Logo
    const logoElement = quidgetHead.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'logo'
    );
    // Action items
    const actionLink = quidgetHead.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'action-link'
    );
    const actionText = quidgetHead.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'action-text'
    );
    // Overview content
    const overviewElement = card?.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'quidget-overview'
    );
    const overviewTitle = overviewElement?.children.find((child: any) => child.name === 'h3');
    const overviewContent = overviewElement?.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'overview-content'
    );
    // Details items
    const detailsElement = card?.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'quidget-details'
    );
    const detailsItems = detailsElement?.children.filter(
      (child: any) => child.name === 'div' && child.attribs?.class === 'details-item'
    );
    const detailsContent = detailsItems?.map((item: any) => {
      let category = item.children.find(
        (child: any) => child.name === 'p' && child.attribs?.class === 'category'
      );
      const value = item.children.find(
        (child: any) => child.name === 'p' && child.attribs?.class === 'value'
      );
      return {
        category: category ? getContent(category) : null,
        value: value ? nodeToHTML(value) : null
      };
    });

    // Why we pick section
    const pickElement = card.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'quidget-pick'
    );
    const pickTitle = pickElement?.children.find((child: any) => child.name === 'h3');
    const pickContent = pickElement?.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'pick-content'
    );

    // Extract accordion if it exists
    const accordionElement = card.children.find(
      (child: any) => child.name === 'div' && child.attribs?.class === 'blog-accordion'
    );

    return {
      id: `quidget-${index}`,
      isPick: card.attribs?.['data-pick'] === 'true',
      tag: card.attribs?.['data-tag'] || null,
      logo: logoElement ? nodeToHTML(logoElement) : null,
      action: {
        link: actionLink ? nodeToHTML(actionLink) : null,
        text: actionText ? nodeToHTML(actionText) : null
      },
      overview: {
        title: overviewTitle ? nodeToHTML(overviewTitle) : null,
        content: overviewContent ? getContent(overviewContent) : null
      },
      details: detailsContent || [],
      pick: {
        title: pickTitle ? nodeToHTML(pickTitle) : null,
        content: pickContent ? getContent(pickContent) : null
      },
      accordion: accordionElement ? generateAccordion(accordionElement) : []
    };
  });

  return quidgetContent;
}
